import React, { useState } from 'react';
import { TableProps, TableColumn } from '../../types';
import { clsx } from 'clsx';
import { ChevronUpIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import LoadingSpinner from './LoadingSpinner';
import { EmptyState } from './AdminPrimitives';

const Table = <T extends Record<string, any>>({
  data,
  columns,
  loading = false,
  emptyMessage = 'No hay datos disponibles',
  className = '',
  responsive = true,
  responsiveBreakpoint = 'md',
  stickyFirstColumn = true,
  mobileTitleKey,
  mobileSubtitleKey,
  mobilePageSize = 2,
  rowClassName = '',
  compact = false,
}: TableProps<T>) => {
  const thPad = compact ? 'px-4 py-2.5' : 'px-6 py-3.5';
  const tdPad = compact ? 'px-4 py-2.5' : 'px-6 py-5';
  const [sortConfig, setSortConfig] = useState<{
    key: keyof T;
    direction: 'asc' | 'desc';
  } | null>(null);

  const handleSort = (key: keyof T) => {
    let direction: 'asc' | 'desc' = 'asc';
    if (sortConfig && sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const sortedData = React.useMemo(() => {
    if (!sortConfig) return data;

    return [...data].sort((a, b) => {
      const aValue = a[sortConfig.key];
      const bValue = b[sortConfig.key];

      if (aValue < bValue) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }, [data, sortConfig]);

  if (loading) {
    return (
      <div className="bg-white overflow-hidden p-8">
        <div className="flex flex-col items-center justify-center gap-3 text-slate-500">
          <LoadingSpinner size="md" color="gray" />
          <p className="text-sm font-medium">Cargando datos...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={clsx('bg-white overflow-hidden', className)}>
      {/* Desktop/tablet table */}
      <div className={clsx('overflow-x-auto hidden', {
        'sm:block': responsiveBreakpoint === 'sm',
        'md:block': responsiveBreakpoint === 'md',
        'lg:block': responsiveBreakpoint === 'lg',
      })}>
        <table className="min-w-full divide-y divide-slate-200">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              {columns.map((column) => (
                <th
                  key={String(column.key)}
                  className={clsx(
                    thPad,
                    'text-left text-xs font-semibold text-slate-700 uppercase tracking-wider',
                    {
                      'cursor-pointer hover:bg-slate-100 transition-colors duration-200': column.sortable,
                      'sticky left-0 z-10 bg-slate-50': stickyFirstColumn && columns[0].key === column.key,
                    },
                    column.className
                  )}
                  onClick={() => column.sortable && handleSort(column.key)}
                >
                  <div className="flex items-center space-x-1">
                    <span>{column.label}</span>
                    {column.sortable && (
                      <div className="flex flex-col">
                        <ChevronUpIcon
                          className={clsx('h-3 w-3', {
                            'text-slate-700': sortConfig?.key === column.key && sortConfig.direction === 'asc',
                            'text-slate-300': !(sortConfig?.key === column.key && sortConfig.direction === 'asc'),
                          })}
                        />
                        <ChevronDownIcon
                          className={clsx('h-3 w-3 -mt-1', {
                            'text-slate-700': sortConfig?.key === column.key && sortConfig.direction === 'desc',
                            'text-slate-300': !(sortConfig?.key === column.key && sortConfig.direction === 'desc'),
                          })}
                        />
                      </div>
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-slate-100">
            {sortedData.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="px-6 py-16 text-center text-slate-500 text-sm">
                  {typeof emptyMessage === 'string' ? (
                    <EmptyState title="Sin resultados" description={emptyMessage} />
                  ) : (
                    emptyMessage
                  )}
                </td>
              </tr>
            ) : (
              sortedData.map((item, index) => (
                <tr key={index} className={clsx('hover:bg-slate-50/60 transition-colors duration-150', rowClassName)}>
                  {columns.map((column) => (
                    <td
                      key={String(column.key)}
                      className={clsx(tdPad, 'whitespace-nowrap text-sm text-slate-900', column.className, {
                        'sticky left-0 z-10 bg-white': stickyFirstColumn && columns[0].key === column.key,
                      })}
                    >
                      {column.render
                        ? column.render(item[column.key], item)
                        : String(item[column.key] || '-')}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Mobile cards */}
      {responsive && (
        <div className={clsx('divide-y divide-gray-200 block pb-16', {
          'sm:hidden': responsiveBreakpoint === 'sm',
          'md:hidden': responsiveBreakpoint === 'md',
          'lg:hidden': responsiveBreakpoint === 'lg',
        })}>
          {sortedData.length === 0 ? (
            <div className="p-6 text-center text-gray-500">
              {typeof emptyMessage === 'string' ? (
                <EmptyState title="Sin resultados" description={emptyMessage} />
              ) : (
                emptyMessage
              )}
            </div>
          ) : (
            <MobileCards<T>
              items={sortedData}
              columns={columns}
              titleKey={mobileTitleKey ?? (columns[0]?.key as keyof T)}
              subtitleKey={mobileSubtitleKey}
              pageSize={mobilePageSize}
            />
          )}
        </div>
      )}
    </div>
  );
};

function MobileCards<T extends Record<string, any>>({
  items,
  columns,
  titleKey,
  subtitleKey,
  pageSize,
}: {
  items: T[];
  columns: TableColumn<T>[];
  titleKey: keyof T;
  subtitleKey?: keyof T;
  pageSize: number;
}) {
  const [page, setPage] = React.useState(1);
  const totalPages = Math.max(1, Math.ceil(items.length / pageSize));

  // Reset/clamp page when the filtered items or page size change
  React.useEffect(() => {
    setPage(1);
  }, [items, pageSize]);

  React.useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [page, totalPages]);
  const startIdx = (page - 1) * pageSize;
  const pageItems = items.slice(startIdx, startIdx + pageSize);

  return (
    <>
      {pageItems.map((item, idx) => {
        const titleCol = columns.find((c) => c.key === titleKey);
        const titleContent = titleCol?.render
          ? titleCol.render(item[titleKey], item)
          : String(item[titleKey] ?? '-');
        const subtitleCol = subtitleKey ? columns.find((c) => c.key === subtitleKey) : undefined;
        const subtitleContent = subtitleCol?.render
          ? subtitleCol.render(item[subtitleKey!], item)
          : subtitleKey ? String(item[subtitleKey] ?? '') : null;

        return (
        <div key={startIdx + idx} className="p-4">
          <div className="rounded-xl border border-gray-200 shadow-sm p-4 bg-white">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="text-sm font-semibold text-gray-900">
                  {titleContent}
                </div>
                {subtitleKey && (
                  <div className="text-xs text-gray-500 mt-0.5">
                    {subtitleContent}
                  </div>
                )}
              </div>
            </div>
            <div className="mt-3 grid grid-cols-1 gap-2">
              {columns.map((col) => {
                if (col.key === titleKey) return null; // omit primary field repeated
                if ((col as any).hideOnMobile) return null;
                return (
                  <div key={String(col.key)} className="text-sm">
                    <div className="text-xs uppercase text-gray-400">{(col as any).mobileLabel || col.label}</div>
                    <div className="text-gray-800">
                      {col.render ? col.render(item[col.key], item) : String(item[col.key] ?? '-')}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
        );
      })}

      {totalPages > 1 && (
        <div className="relative z-40 flex items-center justify-center gap-3 py-4 mb-8 lg:mb-0">
          <button
            type="button"
            className="px-3 py-1.5 rounded-lg border text-sm disabled:opacity-50"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
          >
            Anterior
          </button>
          <div className="text-sm text-gray-600">
            {page} / {totalPages}
          </div>
          <button
            type="button"
            className="px-3 py-1.5 rounded-lg border text-sm disabled:opacity-50"
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
          >
            Siguiente
          </button>
        </div>
      )}
      {/* Spacer to avoid bottom nav overlap */}
      <div className="h-10" />
    </>
  );
}

export default Table;
